import {
  createOnFirstShowNgwAdapter,
  getIdentifyItems,
} from '@nextgis/ngw-kit';
import { unflatten } from '@nextgis/utils';
import { VueNgwControl } from '@nextgis/vue2-ngw-map';
import { Cesium3DTileset } from 'cesium';
import { Component, Vue } from 'vue-property-decorator';

import MapSidebar from './components/MapSidebar/MapSidebar.vue';
import MeasureControl from './components/MeasureControl/MeasureControl.vue';
import { config } from './config';
import { WEBMAP_TERRAIN_ID_PREFIX } from './constants';
import { connector } from './services/connector';
import { quality } from './services/quality';
import { webMapModule } from './store/webMap/webMap';
import { applyUrlParams } from './utils/applyUrlParams';
import { createPopupContentComponent } from './utils/createPopupContentComponent';
import { getVueNgwMapComponent } from './utils/getVueNgwMapComponent';

import type { Scene3DAdapter } from './plugins/threedim/Scene3DAdapter';
import type { MapAdapterOptions } from '@nextgis/cesium-map-adapter';
import type NgwConnector from '@nextgis/ngw-connector';
import type { NgwIdentify } from '@nextgis/ngw-kit';
import type {
  LayerAdapter,
  NgwMap,
  NgwMapOptions,
  VectorLayerAdapter,
} from '@nextgis/ngw-map';
import type { PathPaint } from '@nextgis/paint';
import type { BasemapWebMapItemRead } from '@nextgisweb/basemap/type/api';
import type { CompositeRead } from '@nextgisweb/resource/type/api';
import type { Scene3DTerrainItem } from '@nextgisweb/scene-3d/type/api';
import type { Viewer } from 'cesium';
import type { Feature, GeoJsonObject, MultiPolygon, Polygon } from 'geojson';

@Component({
  components: {
    VueNgwMap: getVueNgwMapComponent,
    VueNgwControl,
    MeasureControl,
    MapSidebar,
  },
})
export default class App extends Vue {
  isLoading = true;
  mapOptions: NgwMapOptions | null = null;

  scene3d: Scene3DAdapter | null = null;
  highlightPaint: PathPaint = {
    color: 'rgb(255,255,0)',
    opacity: 0.6,
    // not work for claim to ground style
    stroke: true,
    weight: 3,
  };

  private _resource: CompositeRead | null = null;
  private _highlightLayers: VectorLayerAdapter[] = [];

  get ngwMap(): NgwMap | null {
    return webMapModule.ngwMap;
  }

  get connector(): () => NgwConnector {
    return () => connector;
  }

  mounted(): void {
    this.loadResource();
  }

  async loadResource(): Promise<void> {
    const opts: NgwMapOptions = {
      // qmsId: 487,
      whitlabel: true,
      target: this.$refs.NgwMap as HTMLElement,
      // minZoom: 3,
      center: [55.7542, 37.6142],
      zoom: 3,
      controls: ['ATTRIBUTION'],
      pixelRadius: 20,
      // controlsOptions: {
      //   ATTRIBUTION: { position: 'bottom-left' }
      // }
    };
    let resourceId: number | undefined;
    const match = window.location.pathname.match(/([0-9]+)\/display/);
    if (match) {
      resourceId = Number(match[1]);
    }
    if (process.env.CLIENT_TYPE === 'solo') {
      resourceId = config.scene3dResource;
    }
    if (resourceId) {
      try {
        const resource = await connector.getResource(resourceId);
        if (resource?.resmeta) {
          const mapAdapterOptions = unflatten(
            resource.resmeta.items,
          ) as MapAdapterOptions;
          const debug = !!resource.resmeta.items.debug;
          if (debug) {
            mapAdapterOptions.viewerCesium3DTilesInspectorMixin = true;
            webMapModule.setDebug(true);
          }
          opts.mapAdapterOptions = mapAdapterOptions;
          if (mapAdapterOptions.highlightLayer) {
            this.highlightPaint = {
              ...this.highlightPaint,
              ...mapAdapterOptions.highlightLayer,
            };
          }
          this._resource = resource;
          const scene3D = resource.scene_3d;
          if (scene3D) {
            quality.value = scene3D.quality;
            const view = scene3D.view;
            switch (view) {
              case 'GLOBUS':
                opts.view = '3D';
                break;
              case 'PLANE':
                opts.view = '2D';
                break;
              // @ts-expect-error COLUMBUS does not included yet
              case 'COLUMBUS':
                opts.view = '2.5D';
                break;
            }
            if (
              scene3D.extent_left !== null &&
              scene3D.extent_bottom !== null &&
              scene3D.extent_right !== null &&
              scene3D.extent_top
            )
              opts.bounds = [
                scene3D.extent_left,
                scene3D.extent_bottom,
                scene3D.extent_right,
                scene3D.extent_top,
              ];
          }
        }
      } catch {
        // ignore
      }
    }

    this.mapOptions = opts;
  }

  async onNgwMapLoad(ngwMap: NgwMap): Promise<void> {
    webMapModule.setMap(ngwMap);

    this.isLoading = false;
    const resource = this._resource;

    if (resource) {
      const basemaps =
        // @ts-ignore
        resource.scene3d_basemap &&
        // @ts-ignore
        (resource.scene3d_basemap.basemaps3d as BasemapWebmapItem[]);
      if (basemaps && basemaps.length) {
        resource.basemap_webmap = {
          basemaps,
        };
      } else {
        ngwMap.addBaseLayer('TILE', {
          url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
          name: 'OpenStreetMap',
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          visibility: true,
          maxZoom: 19,
        });
      }
      const terrains: Scene3DTerrainItem[] | undefined =
        // @ts-ignore
        resource.scene3d_terrain &&
        // @ts-ignore
        resource.scene3d_terrain.terrains3d;
      if (terrains) {
        let onlyOneVisible = false;
        for (const x of terrains) {
          createOnFirstShowNgwAdapter({
            webMap: ngwMap,
            connector,
            item: x as BasemapWebMapItemRead,
            idPrefix: 'scene3dterrain',
          }).then((adapter) => {
            let visibility = false;
            if (!onlyOneVisible && x.enabled) {
              visibility = true;
              onlyOneVisible = true;
            }
            ngwMap.addLayer(adapter, {
              id: WEBMAP_TERRAIN_ID_PREFIX + x.resource_id,
              name: x.display_name,
              visibility,
              terrain: true,
            });
          });
        }
      }

      await ngwMap.addNgwLayer({
        resource: resource.resource.id,
        adapterOptions: {
          selectable: true,
          popupOptions: {
            createPopupContent: ({ feature, target }) => {
              const item = target?.options?.props?.item;
              return feature && createPopupContentComponent(feature, item);
            },
          },
        },
      });
      const highlightLayer = await ngwMap.addGeoJsonLayer({
        paint: this.highlightPaint,
        nativeOptions: {
          watchTerrainChange: false,
        },
      });
      const greenBoxLayer = await ngwMap.addGeoJsonLayer({
        paint: { color: 'rgba(255,255,255,0)', opacity: 0, strokeOpacity: 0 },
        nativeOptions: {
          watchTerrainChange: true,
        },
        popupOptions: {
          createPopupContent: () => {
            return '';
          },
        },
      });
      this._highlightLayers = [highlightLayer, greenBoxLayer];
      this._addScene3DClassificationHighlightEventListener();
      this._addQualityEventListener();
      setTimeout(() => {
        applyUrlParams(ngwMap);
      }, 100);
    }
  }

  private _addQualityEventListener() {
    const ngwMap = this.ngwMap as NgwMap<Viewer>;
    if (ngwMap) {
      const updateCesiumTilesetQuality = (l: LayerAdapter) => {
        const child = l.layer;
        if (Array.isArray(child)) {
          for (const c of child) {
            updateCesiumTilesetQuality(c);
          }
        } else if (child instanceof Cesium3DTileset) {
          child.maximumScreenSpaceError = quality.getMaximumScreenSpaceError();
          ngwMap.mapAdapter.map?.scene.requestRender();
        }
      };
      quality.emitter.on('change', () => {
        const layers = Object.values(ngwMap.allLayers());
        layers.forEach(updateCesiumTilesetQuality);
      });
    }
  }

  private _addScene3DClassificationHighlightEventListener() {
    const ngwMap = this.ngwMap as NgwMap<Viewer>;
    if (ngwMap) {
      ngwMap.emitter.on('preclick', () => {
        ngwMap.cancelPromises();
        for (const l of this._highlightLayers) {
          if (l.unselect) {
            l.unselect();
          }
        }
        for (const l of this._highlightLayers) {
          ngwMap.clearLayerData(l);
        }
      });
      ngwMap.emitter.on('ngw:select', (e) => {
        if (e) {
          ngwMap.fetchIdentifyGeoJson(e).then((resp) => {
            if (resp) {
              const promises = this._highlightLayers.map((l) => {
                let feature2d: GeoJsonObject = resp;
                if (resp.geometry) {
                  if (resp.geometry.type === 'MultiPolygon') {
                    const multiPolygon = feature2d as Feature<MultiPolygon>;
                    multiPolygon.geometry.coordinates =
                      multiPolygon.geometry.coordinates.map((p) =>
                        p.map((pp) => pp.map(([lon, lat]) => [lon, lat])),
                      );
                    feature2d = multiPolygon;
                  } else if (resp.geometry.type === 'Polygon') {
                    const multiPolygon = feature2d as Feature<Polygon>;
                    multiPolygon.geometry.coordinates =
                      multiPolygon.geometry.coordinates.map((p) =>
                        p.map(([lon, lat]) => [lon, lat]),
                      );
                    feature2d = multiPolygon;
                  }
                }
                return ngwMap.setLayerData(l, feature2d);
              });
              Promise.all(promises).then(() => {
                this._highlight(e);
              });
            }
          });
        }
      });
    }
  }

  private async _highlight(e: NgwIdentify) {
    const params = getIdentifyItems(e);
    const param = params[0];
    const highlightLayer = this._highlightLayers[1];
    if (highlightLayer && highlightLayer.select) {
      if (param) {
        const item = await connector.getResource(param.resourceId);
        const popupOptions = highlightLayer.options.popupOptions || {};
        popupOptions.createPopupContent = ({ feature }) => {
          return feature && createPopupContentComponent(feature, item);
          // return feature && createPopupContent(feature, item);
        };
        highlightLayer.options.popupOptions = popupOptions;
      }

      highlightLayer.select();
    }
  }
}
